import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  Image,
  FileText,
  Box,
  Trash2
} from "lucide-react";

export default function ImagesTutorialPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">

          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>

          <div className="flex items-center gap-6">
            <Link
              to="/ajuda/tutoriais"
              className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block"
            >
              Central de Guias
            </Link>

            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>

        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">

        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} />
          <span className="text-indigo-600">Atendimento Clínico</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Galeria de Imagens e Arquivos Clínicos
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span>6 min de leitura</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Atendimento Clínico
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">

          <p className="lead text-xl text-gray-700 font-medium">
            Centralize fotografias clínicas, exames, documentos, escaneamentos intraorais e modelos digitais dos seus pacientes em um único ambiente integrado ao prontuário.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
            Passo a Passo em Texto
          </h2>

          <div className="space-y-8 mt-8">

            {/* Passo 1 */}
            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                1. Acessando a Galeria
              </h3>

              <p>
                Você pode acessar esse módulo de duas formas:
              </p>

              <ul>
                <li>Pelo menu lateral em <strong>Imagens</strong>;</li>
                <li>Pelo <strong>Prontuário do Paciente</strong>, na aba <strong>Imagens</strong>.</li>
              </ul>

              <p>
                Esse módulo centraliza toda a documentação visual do paciente.
              </p>
            </div>


            {/* Passo 2 */}
            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                2. Enviando Arquivos
              </h3>

              <p>
                Clique em <strong>Novo Arquivo</strong> para adicionar conteúdo clínico.
              </p>

              <p>
                O sistema permite armazenar diferentes tipos de arquivos clínicos e administrativos.
              </p>

              <h4 className="font-bold text-gray-900 mt-4">
                Imagens suportadas
              </h4>

              <ul>
                <li>JPG</li>
                <li>PNG</li>
                <li>GIF</li>
                <li>WEBP</li>
              </ul>

              <p>
                Essas imagens podem ser utilizadas para documentação fotográfica do tratamento, fotografias intraorais, fotografias faciais, registros pré e pós-operatórios e acompanhamento da evolução clínica.
              </p>

              <h4 className="font-bold text-gray-900 mt-6">
                Documentos PDF
              </h4>

              <p>
                Também é possível armazenar documentos em PDF, como:
              </p>

              <ul>
                <li>Exames;</li>
                <li>Laudos;</li>
                <li>Relatórios;</li>
                <li>Prescrições;</li>
                <li>Documentos complementares do paciente.</li>
              </ul>

              <p>
                Isso permite manter toda a documentação clínica organizada dentro do OdontoAssist AI.
              </p>
            </div>


            {/* Passo 3 */}
            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                3. Modelos 3D com STL
              </h3>

              <p>
                O OdontoAssist AI também suporta arquivos STL, amplamente utilizados em escaneamentos intraorais, modelos digitais, prótese digital, ortodontia e planejamento clínico.
              </p>

              <p>
                Ao abrir um arquivo STL no visualizador da plataforma, é possível:
              </p>

              <ul>
                <li>Visualizar o modelo em 3D;</li>
                <li>Girar livremente o modelo;</li>
                <li>Aproximar e afastar utilizando zoom;</li>
                <li>Deslocar a visualização do modelo;</li>
                <li>Analisar detalhes anatômicos do escaneamento;</li>
                <li>Inspecionar regiões específicas com maior precisão.</li>
              </ul>

              <div className="bg-slate-50 border rounded-2xl p-5 mt-5">

                <h4 className="font-bold text-gray-900 mb-3">
                  Controles do mouse
                </h4>

                <ul>
                  <li><strong>Botão esquerdo:</strong> gira o modelo em qualquer direção;</li>
                  <li><strong>Scroll:</strong> aproxima ou afasta a visualização (zoom);</li>
                  <li><strong>Botão direito:</strong> desloca a visualização (pan);</li>
                  <li><strong>Scroll pressionado:</strong> também pode ser utilizado para movimentação em alguns navegadores.</li>
                </ul>

              </div>

              <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 mt-5">

                <h4 className="font-bold text-indigo-900 mb-3">
                  Dicas de utilização
                </h4>

                <ul className="text-indigo-900">
                  <li>Utilize o zoom para examinar detalhes anatômicos específicos;</li>
                  <li>Combine rotação e pan para visualizar áreas difíceis de acessar;</li>
                  <li>Em modelos extensos, aproxime primeiro a região desejada;</li>
                  <li>Se o modelo parecer fora da área visível, utilize o zoom para reposicioná-lo.</li>
                </ul>

              </div>

              <p>
                Esse recurso facilita a conferência clínica de escaneamentos e modelos digitais diretamente dentro do OdontoAssist AI, sem necessidade de softwares externos especializados.
              </p>
            </div>


            {/* Passo 4 */}
            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                4. Gerenciando a Galeria
              </h3>

              <p>
                Na página <strong>Imagens</strong>, você pode visualizar arquivos de todos os pacientes em uma galeria centralizada.
              </p>

              <p>
                Também é possível abrir arquivos para conferência ou remover arquivos que não deseja manter.
              </p>
            </div>

            {/* Passo 5 */}

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">

              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                5. Integração com o Prontuário
              </h3>

              <p>
                Quando um arquivo é enviado diretamente pelo prontuário do paciente, ele fica automaticamente vinculado ao histórico clínico daquele paciente.
              </p>

              <p>
                Isso permite centralizar:
              </p>

              <ul>
                <li>Fotografias clínicas;</li>
                <li>Exames;</li>
                <li>Escaneamentos intraorais;</li>
                <li>Modelos STL;</li>
                <li>Laudos;</li>
                <li>Documentação complementar.</li>
              </ul>

              <p>
                Dessa forma, todas as informações visuais relacionadas ao tratamento permanecem organizadas e facilmente acessíveis durante os atendimentos.
              </p>

            </div>

          </div>

          <div className="bg-indigo-900 rounded-3xl p-8 mt-12 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Organização Clínica
            </h3>

            <p>
              Recomenda-se registrar imagens e documentos em todas as etapas importantes do tratamento.
            </p>

            <ul className="mt-4 text-indigo-100">
              <li>Primeira consulta;</li>
              <li>Planejamento do caso;</li>
              <li>Evolução clínica;</li>
              <li>Fases intermediárias;</li>
              <li>Resultado final.</li>
            </ul>

            <p className="mt-4">
              Essa prática melhora significativamente a documentação clínica, facilita comparações ao longo do tratamento e aumenta a rastreabilidade dos atendimentos realizados.
            </p>
          </div>

        </article>

      </main>
    </div>
  );
}
